import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from './IconButton';

// `summary` (story 2-147wb): native `<details>/<summary>` is keyboard-
// focusable by the HTML spec with no `tabindex` attribute of its own, so it
// was invisible to this selector — real browser Tab order would still reach
// it, but THIS component's own custom trap (below) intercepts every Tab
// keypress and re-derives "next focusable" purely from this query, so a
// `<summary>` inside modal content was silently UNREACHABLE by keyboard
// (mouse-clickable, but not tabbable) until this line. Found live moving
// `TemplateCard`'s disclosure into its Details sheet — the sheet's own
// frozen a11y spec (story 2-140) previously described exactly one focusable
// node because nothing else focusable had ever been placed inside a Modal
// before; a real second node now correctly participates in the trap.
export const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

// ------------------------------------------------------------------
// Modal shell — portal at z-90 over a blurred scrim. Esc and backdrop
// click close; focus is trapped inside and returned to the opener; body
// scroll is locked while open.
// ------------------------------------------------------------------
export function Modal({
  children,
  onClose,
  label,
  ariaDescribedBy,
  disableOutsideDismiss = false,
  size = 'md',
  placement = 'center',
}: {
  children: React.ReactNode;
  onClose: () => void;
  /** Accessible name for the dialog. */
  label?: string;
  /** Id of an element describing the dialog, wired to aria-describedby. */
  ariaDescribedBy?: string;
  /** When true, clicking the backdrop does not close the modal (Esc still does). */
  disableOutsideDismiss?: boolean;
  size?: 'md' | 'lg';
  /**
   * Where the panel sits (story 5-10b; `'right'` added by story 2-140).
   *  - `'center'` (default): today's behaviour, byte-identical.
   *  - `'bottom'`: a BOTTOM SHEET — full-width, pinned to the bottom edge, with
   *    only its top corners rounded and a grab handle. P05 §9.2 asks for a
   *    "compact sheet" on mobile, and a sheet is not a different DIALOG: the
   *    focus trap, Esc, scroll lock, backdrop and focus restoration are all
   *    exactly the same contract, and only the geometry differs. Shipping a
   *    separate `Sheet` component would have duplicated every one of those
   *    behaviours — the near-duplicate primitive P18 §6 exists to prevent.
   *    On `sm` and up it recentres, so one control reads correctly on a desktop
   *    too rather than clinging to the bottom of a 1440px window.
   *  - `'right'`: a SIDE SHEET — full-height, pinned to the right edge, with
   *    only its left corners rounded ONCE `widthClass` (`sm:max-w-lg`/
   *    `sm:max-w-2xl`) actually caps its width, i.e. at `sm` and up. Below
   *    `sm` the panel is already `w-full`/`h-full` — edge-to-edge on every
   *    side, a de facto full-screen takeover, not a docked sheet with a
   *    visible right edge to round a left corner against — so the rounding
   *    itself is `sm:rounded-l-2xl`, not unconditional (story 2-147g fix;
   *    unconditional rounding read as a bug at full mobile width: two
   *    rounded corners floating with no adjacent straight edge on either
   *    side to justify them). P10 §2's disclosure rule (story 2-140) names
   *    this the mode for selection details and occasional management (a
   *    "Details" drill-down, a version/share manager) — content that stays
   *    reachable and dismissible over the current page rather than owning a
   *    route. Exactly the same reasoning as `'bottom'` above applies here:
   *    the contract (focus trap/Esc/scroll-lock/backdrop/focus-return) is
   *    identical, only the geometry differs, so this is a THIRD value on the
   *    same primitive, not a second component. Unlike `'bottom'`, it does
   *    NOT reposition to the bottom edge below `sm` — a side sheet stays
   *    right-anchored at every width (2-147g: staying right-docked and
   *    simply going edge-to-edge below `sm`, rather than repositioning to
   *    bottom-sheet geometry, keeps ONE consistent mental model — "this
   *    slides in from the right, dismisses back to the right" — a record's
   *    drill-down/push-navigation feel that would be undermined by
   *    reappearing from the bottom edge on a phone; a bottom sheet's own
   *    edge-to-edge case, by contrast, is capped at `max-h-modal` and
   *    deliberately leaves backdrop showing above it, which is why its
   *    rounding stays correct un-conditionally), capped to `size`'s
   *    max-width so it never spans a wide desktop.
   */
  placement?: 'center' | 'bottom' | 'right';
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const panel = panelRef.current;
    if (panel) {
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    }
    return () => {
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const bottom = placement === 'bottom';
  const right = placement === 'right';
  const widthClass = size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg';

  return createPortal(
    <div
      className={
        bottom
          ? 'fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-4'
          : right
            ? 'fixed inset-0 z-[90] flex items-stretch justify-end'
            : 'fixed inset-0 z-[90] flex items-center justify-center p-4'
      }
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={disableOutsideDismiss ? undefined : onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        className={
          bottom
            ? // `pb-[env(safe-area-inset-bottom)]` keeps the last control clear of
              // the home indicator on a notched phone — P05 §9.2's "sticky
              // navigation never hides controls". `flex flex-col overflow-hidden`
              // (story 2-147g) replaces panel-level `overflow-y-auto` — only
              // `ModalBody` scrolls now, so a header/footer composed via
              // `ModalHeader`/`ModalFooter` stay pinned in view.
              `glass relative flex max-h-modal w-full ${widthClass} flex-col overflow-hidden rounded-t-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:rounded-2xl sm:pb-5`
            : right
              ? // Full-height, edge-anchored at every width (no recentring —
                // unlike `'bottom'`, a side sheet's whole point is staying
                // pinned to the edge it opened from). `pr-[env(safe-area-inset-right)]`
                // mirrors the bottom sheet's safe-area padding for a notched
                // phone in landscape. Rounding is `sm:rounded-l-2xl` (story
                // 2-147g), not unconditional — below `sm` the panel is already
                // `w-full`/`h-full`, edge-to-edge on every side, so there is no
                // adjacent straight edge for a rounded corner to read against
                // until `widthClass` caps the width at `sm`.
                `glass relative flex h-full w-full ${widthClass} flex-col overflow-hidden sm:rounded-l-2xl p-5 pr-[calc(1.25rem+env(safe-area-inset-right))]`
              : `glass relative flex max-h-modal w-full ${size === 'lg' ? 'max-w-2xl' : 'max-w-lg'} flex-col overflow-hidden rounded-2xl p-5`
        }
      >
        {bottom && (
          // The grab handle. Decorative only — the sheet is closed with Esc, the
          // backdrop, or its own control, all of which are already reachable.
          <div className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-border sm:hidden" aria-hidden />
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------------
// Header/body/footer anatomy — story 2-147g. Before this, `Modal` wrapped a
// single padded `<div>` around raw `children` with no shared structure: each
// of its 23 consumers hand-composed its own title/content/action markup,
// which is how `TemplateCard.tsx`'s details sheet shipped with an `<h3>`
// title and NO visible close button at all (Esc/backdrop-click only), while
// `ShareLinksPanel.tsx`'s dialog composed a completely different internal
// shape for the same primitive.
//
// ── API shape: sibling sub-components, not slot props ───────────────────
// `title`/`footer` props on `Modal` itself were considered and rejected: real
// consumer content is JSX-rich (a `Select` + `Textarea` pair, a bulleted
// evidence list, conditional per-state bodies), and three separate slot
// props threading arbitrary ReactNode is just compound components with extra
// indirection. `Form`/`FormField`/`FormError`/`FormSubmit` (`components/ui/
// Form.tsx`) already establish this repo's convention for a primitive with
// several co-operating parts: flat, separately-named exports a consumer
// composes explicitly as JSX children, not a dot-notation `Modal.Header`
// (which nothing else in `components/ui` uses) and not slot props. These
// three follow that exact precedent.
//
// ── `label` (a11y name) vs `title` (visible heading) ─────────────────────
// `Modal`'s own `label` prop stays as the accessible name (`aria-label`),
// unchanged — it is announced the instant the dialog receives focus, before
// any content (including a `ModalHeader`) has necessarily been read, and
// every existing consumer already passes a string here. `ModalHeader`'s
// `title` is a SEPARATE, visible-only heading — most consumers pass the same
// string to both (`label="Create template"` / `<ModalHeader title="Create
// template" .../>`), which reads as duplication but is the correct outcome:
// collapsing them into one (e.g. deriving `aria-label` from `ModalHeader`
// children via a ref/context) would require `ModalHeader` to always render
// and always carry a plain string, which is not true for every consumer
// (a few compose a custom heading element instead of the `title` prop) — the
// simpler, explicit two-prop shape costs one repeated string per consumer
// and buys a header that works whether or not a consumer uses the `title`
// shorthand.
//
// ── Why the close button lives in `ModalHeader`, not `Modal` itself ──────
// Not every `Modal` wants a visible header at all (a couple of consumers
// compose no title/close chrome, relying on Esc/backdrop the same way
// `disableOutsideDismiss` opts OUT of backdrop-close for a destructive
// confirmation) — baking a close button into `Modal` unconditionally would
// force it onto those too. `ModalHeader` is opt-in the same way a consumer
// already opts into `disableOutsideDismiss`; when a consumer renders it, the
// close button comes from `IconButton` (story 2-147c) — the same primitive
// that already fixed this exact gap everywhere else it existed (`Sidebar`,
// `AgentPanel`, `PaywallModal`), just never reached `Modal.tsx` itself since
// `Modal` had no header region for `IconButton` to attach to at that point.

export function ModalHeader({
  title,
  onClose,
  children,
}: {
  /** Visible heading text. Omit and pass a custom heading via `children` instead when the title needs its own markup (an icon, a badge alongside it). */
  title?: string;
  /** Wire to the same `onClose` passed to the enclosing `Modal` — renders the header's close affordance. Omit to render a header with no close button (rare — most dialogs want one; a destructive confirmation that also sets `disableOutsideDismiss` still gets a header close button, since Esc still works and a visible "give up" affordance is still correct there). */
  onClose?: () => void;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        {title && <h2 className="text-lg font-bold text-foreground">{title}</h2>}
        {children}
      </div>
      {onClose && (
        <IconButton
          onClick={onClose}
          aria-label="Close"
          icon={<XMarkIcon className="h-5 w-5" />}
          className="-mr-1 -mt-1 text-muted-foreground hover:text-foreground"
        />
      )}
    </div>
  );
}

// The scrollable region — `flex-1 overflow-y-auto` inside the panel's own
// `flex flex-col overflow-hidden` (above), so a header/footer either side of
// it stay pinned in view regardless of how much content this holds.
// `min-h-0` is required, not decoration: a flex child's default
// `min-height: auto` would let this grow past its share of the column and
// push the footer off-panel instead of scrolling internally, the same
// `min-w-0` discipline `/app/design`'s own content-stress rules already
// apply on the cross axis (P10 §2, story 2-137).
export function ModalBody({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={`min-h-0 flex-1 overflow-y-auto ${className}`}>{children}</div>;
}

// A sticky action row — always the LAST region, never scrolls with the body.
// `border-t border-border` (P10 §1.3's hairline role) is the divider from
// scrolled body content, the same "default hairline between adjacent flat
// content" rule any other card's internal section break already follows.
export function ModalFooter({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={`mt-4 flex shrink-0 justify-end gap-2 border-t border-border pt-4 ${className}`}>{children}</div>
  );
}
